import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";

/**
 * Small state machine + client-side cooldown for the "resend verification email" action.
 * Reused by both the app-wide banner and the /verify-email page.
 *
 * States: "idle" | "sending" | "sent" | "error"
 * The backend endpoint is authenticated + rate-limited at 3/min already; the cooldown
 * here purely prevents accidental double-clicks by the user.
 */
export default function useVerifyResend({ cooldownMs = 60_000 } = {}) {
  const [state, setState] = useState("idle");
  const [message, setMessage] = useState("");
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const startCooldown = useCallback(() => {
    setCooldownLeft(Math.ceil(cooldownMs / 1000));
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldownLeft((s) => {
        if (s <= 1) { clearInterval(timerRef.current); timerRef.current = null; return 0; }
        return s - 1;
      });
    }, 1000);
  }, [cooldownMs]);

  const resend = useCallback(async () => {
    if (state === "sending" || cooldownLeft > 0) return;
    setState("sending");
    setMessage("");
    try {
      const { data } = await api.post("/auth/verify/request", {});
      if (data?.already_verified) {
        setState("sent");
        setMessage("Your email is already verified.");
      } else {
        setState("sent");
        setMessage("Verification email sent. Check your inbox and spam folder.");
      }
      startCooldown();
    } catch (e) {
      setState("error");
      setMessage(
        formatApiError(e?.response?.data?.detail) ||
        "Error sending verification email. Please try again."
      );
    }
  }, [state, cooldownLeft, startCooldown]);

  const reset = useCallback(() => { setState("idle"); setMessage(""); }, []);

  return {
    state,
    message,
    resend,
    reset,
    cooldownLeft,
    isBusy: state === "sending" || cooldownLeft > 0,
  };
}
